/**
 * Billing + payments data access (Epic H), shared by both apps.
 *
 * Gated by VITE_PAYMENTS_ENABLED: when off, the UI keeps the current free
 * order flow and never calls these endpoints.
 */
import { apiFetch } from './api'

/** Build-time flag mirroring the analyzer ANALYZER_PAYMENTS_ENABLED gate. */
export const PAYMENTS_ENABLED =
  (import.meta.env.VITE_PAYMENTS_ENABLED as string | undefined) === 'true'

/** Portuguese IVA rate, kept in sync with the analyzer config (vat_rate). */
export const VAT_RATE = 0.23

export interface BillingProfile {
  nif: string | null
  billing_name: string | null
  address: string | null
  city: string | null
  postal_code: string | null
  country: string
}

export interface OrderPayment {
  order_id: string
  payment_status: string // unpaid | pending | paid | failed | refunded
  total_cents: number | null
  vat_cents: number | null
  amount_paid_cents: number | null
  currency: string | null
  payment_method: string | null
  invoice_url: string | null
  paid_at: string | null
}

export interface CheckoutSession {
  url: string
  session_id: string
}

export const billingService = {
  async getBillingProfile(): Promise<{ profile: BillingProfile | null; error: Error | null }> {
    const { data, error } = await apiFetch<BillingProfile | null>('/billing-profile')
    return { profile: data, error }
  },

  async putBillingProfile(
    body: Partial<BillingProfile>,
  ): Promise<{ profile: BillingProfile | null; error: Error | null }> {
    const { data, error } = await apiFetch<BillingProfile>('/billing-profile', {
      method: 'PUT',
      body: JSON.stringify(body),
    })
    return { profile: data, error }
  },

  /**
   * Create a Stripe Checkout Session for an unpaid order. The optional billing
   * body is upserted into the user's default billing profile server-side.
   */
  async createCheckoutSession(
    orderId: string,
    billing?: Partial<BillingProfile>,
  ): Promise<{ session: CheckoutSession | null; error: Error | null }> {
    const { data, error } = await apiFetch<CheckoutSession>(
      `/orders/${orderId}/checkout-session`,
      { method: 'POST', body: JSON.stringify(billing ?? {}) },
    )
    return { session: data, error }
  },

  async getOrderPayment(
    orderId: string,
  ): Promise<{ payment: OrderPayment | null; error: Error | null }> {
    const { data, error } = await apiFetch<OrderPayment>(`/orders/${orderId}/payment`)
    return { payment: data, error }
  },
}
